import ThreeCAuthService from './ThreeCAuthService'
import ThreeCSocketService from './ThreeCSocketService'
import ThreeCApiService from './ThreeCApiService'
import ThreeCCallsRepository from '@/shared/http/repositories/threec/calls'
import threeCConfig from './ThreeCConfig'

/**
 * Serviço principal da integração 3C Plus VoIP
 * Orquestra todo o fluxo: Auth → Iframe → Socket → Login → Calls
 */
class ThreeCVoipService {
  constructor () {
    this.isInitialized = false
    this.isAgentLoggedIn = false
    this.isInManualMode = false
    this.currentCallId = null
    this.currentCampaignId = null
    this.iframe = null
    this.eventCallbacks = new Map()
  }

  /**
   * Inicializa a integração completa seguindo a ordem recomendada
   * Ordem: Configuração → Autenticação → Iframe WebRTC (ramal) → Socket.io
   * @returns {Promise<void>}
   */
  async initialize () {
    try {
      // Garantir que a configuração está carregada antes de prosseguir
      await this.ensureConfigLoaded()

      // ⚠️ VERIFICAÇÃO CRÍTICA: Não continuar se o serviço não estiver habilitado
      if (!threeCConfig.isEnabled) {
        throw new Error('Integração 3C Plus está desabilitada globalmente')
      }

      if (!threeCConfig.isUserEnabled) {
        throw new Error('Usuário não possui 3C Plus habilitado')
      }

      await this.authenticate()
      await this.loadWebRTCIframe()
      await this.connectSocket()

      this.isInitialized = true
      this.emit('initialized')
    } catch (error) {
      this.emit('error', error)
      throw error
    }
  }

  /**
   * Garante que a configuração do 3C Plus está carregada
   * @returns {Promise<void>}
   */
  async ensureConfigLoaded () {
    try {
      // Sempre recarregar a configuração para garantir que está atualizada

      // Obter o ID do usuário atual
      const { JWTService } = await import('@/shared/services/jwt')
      const userId = JWTService.GetUserId()

      const configLoaded = await threeCConfig.init(userId)

      // Verificar se a configuração foi carregada com sucesso
      if (!configLoaded || !threeCConfig.isLoaded || !threeCConfig.config) {
        throw new Error('Configuração 3C Plus não disponível no backend')
      }

      console.log('✅ Configuração 3C Plus carregada e verificada:', {
        domain: threeCConfig.DOMAIN,
        apiBaseUrl: threeCConfig.API_BASE_URL,
        socketUrl: threeCConfig.SOCKET_URL,
        enabled: threeCConfig.isEnabled,
        userConfig: threeCConfig.userConfig
      })
    } catch (error) {
      throw new Error(`Falha ao carregar configuração 3C Plus: ${error.message}`)
    }
  }

  async authenticate () {
    console.log('🔐 Iniciando autenticação 3C Plus com:', {
      domain: threeCConfig.DOMAIN,
      apiBaseUrl: threeCConfig.API_BASE_URL,
      user: threeCConfig.USER
    })

    // Limpar token anterior se existir (para forçar nova autenticação)
    ThreeCAuthService.clearToken()

    await ThreeCAuthService.authenticate()

    console.log('✅ Autenticação 3C Plus concluída com sucesso')
  }

  async loadWebRTCIframe () {
    await this.requestMicrophonePermission()

    return new Promise((resolve, reject) => {
      const token = ThreeCAuthService.getToken()
      if (!token) {
        reject(new Error('Token não disponível para carregar iframe'))
        return
      }

      this.iframe = document.createElement('iframe')
      this.iframe.src = `https://${threeCConfig.DOMAIN}/extension?api_token=${token}`
      this.iframe.width = '1'
      this.iframe.height = '1'
      this.iframe.style.position = 'absolute'
      this.iframe.style.left = '-9999px'
      this.iframe.style.top = '-9999px'
      this.iframe.style.opacity = '0'
      this.iframe.style.pointerEvents = 'none'
      this.iframe.allow = 'microphone'

      this.iframe.onload = () => {
        resolve()
      }

      this.iframe.onerror = (error) => {
        console.error('❌ Erro ao carregar iframe WebRTC:', error)
        reject(new Error('Falha ao carregar ramal WebRTC. Verifique se o ramal está registrado e se as permissões do microfone estão configuradas.'))
      }

      setTimeout(() => {
        reject(new Error('Timeout ao carregar ramal WebRTC. Verifique se o ramal está registrado e aceitando conexões.'))
      }, threeCConfig.IFRAME_LOAD_TIMEOUT)

      document.body.appendChild(this.iframe)
    })
  }

  async requestMicrophonePermission () {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        console.warn('⚠️ getUserMedia não disponível neste navegador')
        return
      }

      console.log('🎤 Solicitando permissão do microfone...')
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false
      })

      console.log('✅ Permissão do microfone concedida')
      stream.getTracks().forEach(track => track.stop())
    } catch (error) {
      console.error('❌ Erro ao acessar microfone:', error)

      if (error.name === 'NotAllowedError') {
        console.warn('⚠️ Permissão do microfone negada pelo usuário')
        // Não bloquear o fluxo, apenas avisar
      } else if (error.name === 'NotFoundError') {
        console.warn('⚠️ Microfone não encontrado')
      } else if (error.name === 'NotReadableError') {
        console.warn('⚠️ Microfone está sendo usado por outro aplicativo')
      } else {
        console.warn(`⚠️ Erro ao acessar microfone: ${error.message}`)
      }
    }
  }

  async connectSocket () {
    const token = ThreeCAuthService.getToken()
    await ThreeCSocketService.connect(token)

    this.setupSocketListeners()
  }

  setupSocketListeners () {
    ThreeCSocketService.on('agent-is-connected', (payload) => {
      this.isAgentLoggedIn = true
      this.emit('agent-connected', payload)
      this.notifyBackendAgentStatus('CONNECTED', payload)
    })

    ThreeCSocketService.on('agent-is-idle', (payload) => {
      this.isAgentLoggedIn = true
      this.emit('agent-idle', payload)
      this.notifyBackendAgentStatus('IDLE', payload)
    })

    ThreeCSocketService.on('agent-login-failed', (payload) => {
      this.isAgentLoggedIn = false
      this.emit('agent-login-failed', payload)
    })

    ThreeCSocketService.on('agent-was-logged-out', (payload) => {
      this.isAgentLoggedIn = false
      this.isInManualMode = false
      this.emit('agent-logged-out', payload)
      this.notifyBackendAgentStatus('LOGGED_OUT', payload)
    })

    ThreeCSocketService.on('agent-entered-manual', (payload) => {
      this.isInManualMode = true
      this.emit('manual-mode-entered', payload)
    })

    ThreeCSocketService.on('agent-in-acw', (payload) => {
      this.emit('agent-in-acw', payload)
      this.notifyBackendAgentStatus('ACW', payload)
    })

    ThreeCSocketService.on('agent-entered-work-break', (payload) => {
      this.emit('agent-work-break', payload)
      this.notifyBackendAgentStatus('BREAK', payload)
    })

    ThreeCSocketService.on('agent-left-work-break', (payload) => {
      this.emit('agent-left-work-break', payload)
      this.notifyBackendAgentStatus('IDLE', payload)
    })

    // Eventos de chamadas
    ThreeCSocketService.on('call-was-created', (payload) => {
      console.log('📞 Chamada criada:', payload)
      if (payload.call) {
        this.currentCallId = payload.call.id || payload.call._id
      }
      this.emit('call-created', payload)
      this.notifyBackendIncomingCall(payload)
    })

    ThreeCSocketService.on('call-was-answered', (payload) => {
      console.log('📞 Chamada atendida:', payload)
      this.emit('call-answered', payload)
    })

    ThreeCSocketService.on('call-was-connected', (payload) => {
      if (payload.call) {
        this.currentCallId = payload.call.id || payload.call._id
      } else if (payload.id) {
        this.currentCallId = payload.id
      }
      console.log('✅ Chamada conectada:', payload)
      this.emit('call-connected', payload)
      this.notifyBackendCallConnected(payload)
    })

    ThreeCSocketService.on('call-was-finished', (payload) => {
      console.log('📴 Chamada finalizada:', payload)
      this.emit('call-finished', payload)
      this.notifyBackendCallEnded(payload)
      this.currentCallId = null
    })

    ThreeCSocketService.on('call-was-ended', (payload) => {
      console.log('📴 Chamada encerrada:', payload)
      this.emit('call-ended', payload)
      this.notifyBackendCallEnded(payload)
      this.currentCallId = null
    })

    ThreeCSocketService.on('call-was-abandoned', (payload) => {
      console.log('⚠️ Chamada abandonada:', payload)
      this.emit('call-abandoned', payload)
      this.notifyBackendCallEnded(payload)
      this.currentCallId = null
    })

    ThreeCSocketService.on('call-was-not-answered', (payload) => {
      console.log('📵 Chamada não foi atendida:', payload)
      this.emit('call-was-not-answered', payload)
      this.notifyBackendCallEnded(payload)
      this.currentCallId = null
    })

    ThreeCSocketService.on('call-history-was-created', (payload) => {
      this.saveCallToDatabase(payload)
    })
  }

  /**
   * Notifica o backend sobre status do agente via WebSocket Spring
   */
  notifyBackendAgentStatus (status, payload) {
    try {
      if (typeof window !== 'undefined' && window.stompClient && window.stompClient.connected) {
        const message = {
          status: status,
          agentId: (payload.agent && payload.agent.id) || (payload.agent && payload.agent._id),
          campaignId: (payload.campaign && payload.campaign.id) || (payload.campaign && payload.campaign._id) || this.currentCampaignId,
          timestamp: new Date().toISOString(),
          data: payload
        }
        window.stompClient.send('/app/threec/agent-status', {}, JSON.stringify(message))
        console.log('📤 Status do agente enviado ao backend:', status)
      }
    } catch (error) {
      console.warn('⚠️ Erro ao notificar backend sobre status do agente:', error)
    }
  }

  /**
   * Notifica o backend sobre chamada recebida via WebSocket Spring
   */
  notifyBackendIncomingCall (payload) {
    try {
      if (typeof window !== 'undefined' && window.stompClient && window.stompClient.connected) {
        const message = {
          callId: (payload.call && payload.call.id) || (payload.call && payload.call._id) || payload.id,
          phoneNumber: (payload.call && payload.call.phone_number) || (payload.call && payload.call.number),
          campaignId: (payload.campaign && payload.campaign.id) || (payload.campaign && payload.campaign._id) || this.currentCampaignId,
          agentId: (payload.agent && payload.agent.id) || (payload.agent && payload.agent._id),
          timestamp: new Date().toISOString(),
          data: payload
        }
        window.stompClient.send('/app/threec/incoming-call', {}, JSON.stringify(message))
        console.log('📤 Chamada recebida enviada ao backend:', message.callId)
      }
    } catch (error) {
      console.warn('⚠️ Erro ao notificar backend sobre chamada recebida:', error)
    }
  }

  /**
   * Notifica o backend sobre chamada conectada via WebSocket Spring
   */
  notifyBackendCallConnected (payload) {
    try {
      if (typeof window !== 'undefined' && window.stompClient && window.stompClient.connected) {
        const message = {
          callId: (payload.call && payload.call.id) || (payload.call && payload.call._id) || payload.id || this.currentCallId,
          agentId: (payload.agent && payload.agent.id) || (payload.agent && payload.agent._id),
          campaignId: (payload.campaign && payload.campaign.id) || (payload.campaign && payload.campaign._id) || this.currentCampaignId,
          timestamp: new Date().toISOString(),
          data: payload
        }
        window.stompClient.send('/app/threec/call-connected', {}, JSON.stringify(message))
        console.log('📤 Chamada conectada enviada ao backend:', message.callId)
      }
    } catch (error) {
      console.warn('⚠️ Erro ao notificar backend sobre chamada conectada:', error)
    }
  }

  /**
   * Notifica o backend sobre chamada finalizada via WebSocket Spring
   */
  notifyBackendCallEnded (payload) {
    try {
      if (typeof window !== 'undefined' && window.stompClient && window.stompClient.connected) {
        const message = {
          callId: (payload.call && payload.call.id) || (payload.call && payload.call._id) || payload.id || this.currentCallId,
          agentId: (payload.agent && payload.agent.id) || (payload.agent && payload.agent._id),
          campaignId: (payload.campaign && payload.campaign.id) || (payload.campaign && payload.campaign._id) || this.currentCampaignId,
          timestamp: new Date().toISOString(),
          data: payload
        }
        window.stompClient.send('/app/threec/call-ended', {}, JSON.stringify(message))
        console.log('📤 Chamada finalizada enviada ao backend:', message.callId)
      }
    } catch (error) {
      console.warn('⚠️ Erro ao notificar backend sobre chamada finalizada:', error)
    }
  }

  async saveCallToDatabase (payload) {
    const { JWTService } = await import('@/shared/services/jwt')
    const userId = JWTService.GetUserId()

    let phoneNumber = payload.callHistory.number
    if (phoneNumber.startsWith('55')) {
      phoneNumber = phoneNumber.substring(2)
    }

    const callPayload = {
      callId: payload.callHistory._id,
      phoneNumber: phoneNumber,
      userId: userId || 1
    }

    await ThreeCCallsRepository.saveCall(callPayload)
  }

  async getCampaigns () {
    try {
      const campaigns = await ThreeCApiService.getCampaigns()
      this.emit('campaigns-loaded', campaigns)
      return campaigns
    } catch (error) {
      this.emit('error', error)
      throw error
    }
  }

  async loginAgent (campaignId) {
    try {
      this.currentCampaignId = campaignId

      if (!this.iframe) {
        throw new Error('Iframe WebRTC não foi carregado. Execute initialize() primeiro.')
      }

      if (!ThreeCSocketService.isConnected()) {
        throw new Error('Socket.io não está conectado. Execute initialize() primeiro.')
      }

      try {
        await ThreeCApiService.agentLogin(campaignId)
        await this.waitForLoginConfirmation()
      } catch (error) {
        if (error.message === 'AGENT_ALREADY_LOGGED_IN') {
          await this.handleAgentAlreadyLoggedIn(campaignId)
        } else {
          throw error
        }
      }
    } catch (error) {
      this.emit('error', error)
      throw error
    }
  }

  async waitForLoginConfirmation () {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Timeout aguardando confirmação do login. Verifique se o ramal está registrado e aceitando conexões.'))
      }, 30000)

      const onSuccess = (payload) => {
        clearTimeout(timeout)
        ThreeCSocketService.off('agent-is-idle', onSuccess)
        ThreeCSocketService.off('agent-is-connected', onSuccess)
        ThreeCSocketService.off('agent-login-failed', onFailure)
        resolve()
      }

      const onFailure = (payload) => {
        clearTimeout(timeout)
        ThreeCSocketService.off('agent-is-idle', onSuccess)
        ThreeCSocketService.off('agent-is-connected', onSuccess)
        ThreeCSocketService.off('agent-login-failed', onFailure)
        reject(new Error(`Login falhou: ${payload.message || 'Erro desconhecido'}`))
      }

      ThreeCSocketService.once('agent-is-idle', onSuccess)
      ThreeCSocketService.once('agent-is-connected', onSuccess)

      ThreeCSocketService.once('agent-login-failed', onFailure)
    })
  }

  async handleAgentAlreadyLoggedIn (campaignId) {
    await ThreeCApiService.agentLogout()

    await new Promise((resolve) => {
      const onLoggedOut = () => {
        ThreeCSocketService.off('agent-was-logged-out', onLoggedOut)
        resolve()
      }

      ThreeCSocketService.once('agent-was-logged-out', onLoggedOut)
    })

    await new Promise(resolve => setTimeout(resolve, 1000))

    await ThreeCApiService.agentLogin(campaignId)

    await this.waitForLoginConfirmation()
  }

  async logoutAgent () {
    try {
      await ThreeCApiService.agentLogout()
      this.isAgentLoggedIn = false
      this.isInManualMode = false
      this.currentCallId = null
      this.currentCampaignId = null
    } catch (error) {
      this.emit('error', error)
      throw error
    }
  }

  async enterManualMode () {
    try {
      await ThreeCApiService.enterManualMode()
    } catch (error) {
      this.emit('error', error)
      throw error
    }
  }

  async enterWorkBreak (workBreakId = 1) {
    try {
      await ThreeCApiService.enterWorkBreak(workBreakId)
    } catch (error) {
      this.emit('error', error)
      throw error
    }
  }

  async exitWorkBreak () {
    try {
      await ThreeCApiService.exitWorkBreak()
    } catch (error) {
      this.emit('error', error)
      throw error
    }
  }

  async dialNumber (phoneNumber) {
    try {
      const formattedNumber = ThreeCApiService.formatPhoneNumber(phoneNumber)

      const response = await ThreeCApiService.dialNumber(formattedNumber)

      if (response.call.id) {
        this.currentCallId = response.call.id
      }

      return response
    } catch (error) {
      this.emit('error', error)
      throw error
    }
  }

  async hangupCall () {
    if (!this.currentCallId) {
      throw new Error('Nenhuma chamada ativa para desligar')
    }

    try {
      console.log(`Desligando chamada ${this.currentCallId}...`)
      await ThreeCApiService.hangupCall(this.currentCallId)
      this.currentCallId = null
    } catch (error) {
      this.emit('error', error)
      throw error
    }
  }

  on (event, callback) {
    if (!this.eventCallbacks.has(event)) {
      this.eventCallbacks.set(event, [])
    }
    this.eventCallbacks.get(event).push(callback)
  }

  off (event, callback) {
    if (!this.eventCallbacks.has(event)) return

    const callbacks = this.eventCallbacks.get(event)
    const index = callbacks.indexOf(callback)
    if (index > -1) {
      callbacks.splice(index, 1)
    }
  }

  emit (event, data) {
    if (this.eventCallbacks.has(event)) {
      this.eventCallbacks.get(event).forEach(callback => {
        try {
          callback(data)
        } catch (error) {
        }
      })
    }
  }

  disconnect () {
    ThreeCSocketService.disconnect()

    if (this.iframe && this.iframe.parentNode) {
      this.iframe.parentNode.removeChild(this.iframe)
      this.iframe = null
    }

    ThreeCAuthService.clearToken()

    this.isInitialized = false
    this.isAgentLoggedIn = false
    this.isInManualMode = false
    this.currentCallId = null
    this.currentCampaignId = null
    this.eventCallbacks.clear()
  }

  get initialized () { return this.isInitialized }
  get agentLoggedIn () { return this.isAgentLoggedIn }
  get inManualMode () { return this.isInManualMode }
  get callId () { return this.currentCallId }
  get campaignId () { return this.currentCampaignId }
}

export default new ThreeCVoipService()



// WEBPACK FOOTER //
// ./src/shared/services/voip/ThreeCVoipService.js